export function omitReduxProps(props = {}) {
    const clonedObject = Object.assign({}, props)
    delete clonedObject.history
    delete clonedObject.location
    delete clonedObject.match
    delete clonedObject.staticContext
    delete clonedObject.dispatch

    return clonedObject
}

export function isUnauthenticated(error) {
    const status = (error?.response && error?.response?.status) ? error.response.status : null;
    const unauthorizedErrors = ["The user is not authenticated", "No current user", "Request failed with status code 401"];
    
    return unauthorizedErrors.includes(error) || status === 401
}

export function getSearchParam() {
  return ((window.location.pathname.indexOf("/search/") === 0 && window.location.pathname.split("/").slice(-1)?.[0]) || undefined) ?? "";
}

export function getApplDetailParams() {
    const parts = window.location.pathname.split('/').filter(Boolean);
    // Only the two applicant-detail URL shapes yield params. Any other page (e.g. the Scholars queue at
    // /scholars/queue/{subProgramId}) returns empty so callers' `skip: !applicantEmployeeId` guards prevent
    // firing applicant-scoped queries with a bogus id (a non-applicant path segment like "queue").
    // /scholars/applicant/{applicantEmployeeId}/{term}     → parts: [scholars, applicant, emplid, term]
    if (parts[0] === 'scholars' && parts[1] === 'applicant') {
        return { applicantEmployeeId: parts[2], programId: 'scholars', term: parts[3] };
    }
    // /applicant/{applicantEmployeeId}/{programId}/{term}  → parts: [applicant, emplid, programId, term]
    if (parts[0] === 'applicant') {
        return { applicantEmployeeId: parts[1], programId: parts[2], term: parts[3] };
    }
    return { applicantEmployeeId: undefined, programId: undefined, term: undefined };
}

/**
 * has507 — Build the correct applicant detail URL based on programId.
 * Scholars applicants route to /scholars/applicant/{emplid}/{term} (scholars-specific page).
 * Honors applicants route to /applicant/{emplid}/{programId}/{term} (shared page).
 */
export function buildApplicantDetailUrl(applicantEmployeeId, programId, term) {
    if (programId === "scholars") {
        return `/scholars/applicant/${applicantEmployeeId}/${term}`;
    }
    return `/applicant/${applicantEmployeeId}/${programId}/${term}`;
}

export const sortByAttribute = (array, key ) => {
    return array.sort((a,b) => {
      const valA = a[key];
      const valB = b[key];
      if(valA < valB){
        return -1;
      } else if(valB > valA)
      return 1;
       else {
        return 0;
       }
    })
  }
  
export function formatDatetime(dttm) { return dttm ? new Date(dttm).toLocaleString() : "-"; }

// Single source of truth for rendering a date value in a datagrid cell as a human-readable date.
// Uses the same locale format as the report/searchDetails transformer (toLocaleDateString(), e.g.
// "12/12/2025") so Scholars grids match the existing Honors date display. Non-date / empty values
// pass through unchanged so it is safe on any column.
export function formatGridDate(value) {
    if (value === null || value === undefined || value === "") { return value; }
    const date = new Date(value);
    if (isNaN(date.getTime())) { return value; }
    return date.toLocaleDateString();
}

export function getSevenDaysFromToday() {
    let sevenDaysFromToday = new Date();
    sevenDaysFromToday.setDate(sevenDaysFromToday.getDate() + 7);
    sevenDaysFromToday.setHours(0);
    sevenDaysFromToday.setMinutes(0);
    sevenDaysFromToday.setSeconds(0);
    sevenDaysFromToday.setMilliseconds(0);
    return sevenDaysFromToday;
}

export function addBusinessDays(startDate, businessDays) {
  const date = new Date(startDate);
  let daysAdded = 0;
  
  while (daysAdded < businessDays) {
    const dayOfWeek = date.getDay(); 
    
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
    
    if (daysAdded < businessDays) {
      date.setDate(date.getDate() + 1);
    }
  }
  
  date.setHours(17, 0, 0, 0);
  
  return date;
}

export const getDefaultGradingTemplate = (gradingTemplates) => {
    const templateArray = Object.values(gradingTemplates)
    return (gradingTemplates && templateArray.length === 1) ? templateArray[0].key : {}
}

export function addSpacesToCamelCase(str) {
    return str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

// Turn a raw backend enum value into a human-readable label.
//  - If `map` is provided and contains the value, return the mapped label (single source of truth).
//  - Otherwise fall back to a safe SCREAMING_SNAKE humanizer: "SCHOLARS_IN_REVIEW" -> "Scholars in review".
//    (Unlike addSpacesToCamelCase, this handles UPPER_SNAKE without corrupting it.)
//  - Empty/dash/non-string values pass through unchanged so it is safe on any cell or option.
export function humanizeEnum(value, map) {
    if (value === null || value === undefined || value === '' || value === '-' || value === '—') {
        return value;
    }
    if (typeof value !== 'string') { return value; }
    if (map && Object.prototype.hasOwnProperty.call(map, value) && map[value] != null) {
        return map[value];
    }
    // Only humanize values that look like enum codes (contain an underscore or are all-caps). Leave
    // already-friendly strings (e.g. a free-text reason, or a backend value that is already a label) alone.
    const looksLikeCode = /_/.test(value) || value === value.toUpperCase();
    if (!looksLikeCode) { return value; }
    const spaced = value.replace(/_/g, ' ').trim().toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Convenience: resolve the right label map for a datagrid column field (or Advanced Search attribute key)
// from ENUM_FIELD_LABEL_MAPS, then humanize. Fields with no registered map still get the safe fallback.
export function humanizeEnumByField(field, value, fieldMaps) {
    const map = fieldMaps && field ? fieldMaps[field] : undefined;
    return humanizeEnum(value, map);
}

export function conjugate(type, count) {
  switch(type) {
    case 'isAre':
      return count === 1 ? 'is' : 'are'
    case 'trailingS':
      return count === 1 ? '' : 's'
    default:
      return null;
  }
}

export function openBypassPopupBlocker(url) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

